import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Divider, Typography, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getIn, useFormik } from 'formik';
import * as yup from 'yup';
import { AnyObject, TestContext } from 'yup';
import { DateTime } from 'luxon';

import {
  BannerFormData,
  BannerScope,
  BannerScopeOption,
  BannerType,
  BannerTypeOption,
} from '../../../../models/BannersModels';
import { Market } from '../../../../models/MarketsModels';
import { DefaultInputLength, DefaultTextboxLength } from '../../../../configs/RegexConfig';
import { RootState } from '../../../../stores/Store';
import { updateBannerForm } from '../../../../stores/BannersStore';

import Button from '../../../../components/Button/Button';
import ButtonGroup from '../../../../components/ButtonGroup/ButtonGroup';
import TextInput from '../../../../components/TextInput/TextInput';
import Dropdown from '../../../../components/Dropdown/Dropdown';
import MultipleDropdown from '../../../../components/MultipleDropdown/MultipleDropdown';
import { BannerCalendarIcon, BannerWarningIcon, BannerPencilIcon } from '../../../../components/Icons/BannerFormIcons';
import DateTimeCalendar from '../../../../components/DateTime/DateTime';
import BannerOptionCard from './BannerOptionCard';
import FormSection from './FormSection';
import TimeZoneConversionTable from './TimeZoneConversionTable';
import MessagePreview from './MessagePreview';
import useBannersFormContainer from './useBannersFormContainer';
import { Switch } from './BannerFormContainerStyle';

interface BannersFormContainerProps {
  onClose: () => void;
  nextStep: () => void;
  editMode: boolean;
  isLoading: boolean;
  onSaveDraft: (values: BannerFormData) => void;
}

function BannersFormContainer({ onClose, nextStep, editMode, isLoading, onSaveDraft }: BannersFormContainerProps) {
  const [t] = useTranslation();
  const theme = useTheme();
  const { timezoneOptions, companies, companiesOptions, marketOptions, markets } = useBannersFormContainer();
  const initialFormData = useSelector((state: RootState) => state.bannersReducer.form);
  const dispatch = useDispatch();

  /**
   * Validates date range
   * @param date
   * @param ctx
   * @param otherDateField
   */
  const validateDateRange = (date: string | null | undefined, ctx: TestContext<AnyObject>, otherDateField: string) => {
    const otherDate = ctx.parent[otherDateField] as string | null;
    if (date && otherDate && DateTime.fromISO(date) < DateTime.fromISO(otherDate)) {
      return ctx.createError({ message: t('common.inputValidations.endDateBeforeStartDate') });
    }
    return true;
  };

  /**
   * Validates if a date is not before today
   * @param date
   * @param ctx
   * @param timezoneField
   */
  const validateDateNotBeforeToday = (
    date: string | null | undefined,
    ctx: TestContext<AnyObject>,
    timezoneField: string,
  ) => {
    const timezone = ctx.parent[timezoneField] as string;
    const dateTime = date ? DateTime.fromISO(date) : null;
    const zonedDateTime = dateTime && timezone ? dateTime.setZone(timezone, { keepLocalTime: true }) : dateTime;
    if (zonedDateTime && zonedDateTime < DateTime.now()) {
      return ctx.createError({ message: t('common.inputValidations.startDateBeforeToday') });
    }
    return true;
  };

  /**
   * Converts a formik-stored ISO date string back into a DateTime for the calendar inputs
   * @param value
   */
  const toDateTimeValue = (value: string | null) => (value ? DateTime.fromISO(value) : null);

  /**
   * Required only when the banner type is Planned change, optional (and unvalidated) otherwise
   * @param schema
   */
  const requiredWhenPlannedChange = <T extends yup.AnySchema>(schema: T) =>
    schema.when('bannerType', ([bannerType]: BannerType[], resolvedSchema: T) => {
      if (bannerType === BannerType.PLANNED_CHANGE)
        return resolvedSchema.required(t('common.inputValidations.requiredField'));
      return resolvedSchema;
    });

  const formik = useFormik({
    initialValues: initialFormData,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: editMode ? !isLoading : true,
    enableReinitialize: true,
    validationSchema: yup.object({
      bannerType: yup.string().required(t('common.inputValidations.requiredField')),
      scope: yup.string().required(t('common.inputValidations.requiredField')),
      selectedMarkets: yup
        .array()
        .of(yup.string())
        .when('scope', ([scope], schema) => {
          if (scope === BannerScope.MARKETS) {
            return schema
              .min(1, t('common.inputValidations.requiredField'))
              .required(t('common.inputValidations.requiredField'));
          }
          return schema;
        }),
      selectedCompanies: yup
        .array()
        .of(yup.string())
        .when('scope', ([scope], schema) => {
          if (scope === BannerScope.COMPANIES) {
            return schema
              .min(1, t('common.inputValidations.requiredField'))
              .required(t('common.inputValidations.requiredField'));
          }
          return schema;
        }),
      bannerTitle: yup
        .string()
        .required(t('common.inputValidations.requiredField'))
        .max(DefaultInputLength, t('common.inputValidations.maxLength', { len: DefaultInputLength })),
      timezone: yup.string().when('bannerType', {
        is: (val: string) => val === BannerType.PLANNED_CHANGE,
        then: () => yup.string().required(t('common.inputValidations.requiredField')),
        otherwise: () => yup.string().notRequired(),
      }),
      startDate: requiredWhenPlannedChange(yup.string().nullable()).test(
        'notBeforeToday',
        t('common.inputValidations.startDateBeforeToday'),
        (date, ctx) => validateDateNotBeforeToday(date, ctx, 'timezone'),
      ),
      endDate: requiredWhenPlannedChange(yup.string().nullable())
        .test('notBeforeToday', t('common.inputValidations.startDateBeforeToday'), (date, ctx) =>
          validateDateNotBeforeToday(date, ctx, 'timezone'),
        )
        .test('dateRange', t('common.inputValidations.endDateBeforeStartDate'), (date, ctx) =>
          validateDateRange(date, ctx, 'startDate'),
        ),
      maintenanceTimezone: requiredWhenPlannedChange(yup.string()),
      maintenanceStart: requiredWhenPlannedChange(yup.string().nullable()).test(
        'notBeforeToday',
        t('common.inputValidations.startDateBeforeToday'),
        (date, ctx) => validateDateNotBeforeToday(date, ctx, 'maintenanceTimezone'),
      ),
      maintenanceEnd: requiredWhenPlannedChange(yup.string().nullable())
        .test('notBeforeToday', t('common.inputValidations.startDateBeforeToday'), (date, ctx) =>
          validateDateNotBeforeToday(date, ctx, 'maintenanceTimezone'),
        )
        .test('dateRange', t('common.inputValidations.endDateBeforeStartDate'), (date, ctx) =>
          validateDateRange(date, ctx, 'maintenanceStart'),
        ),
      messages: yup.mixed<Record<string, string>>().when('bannerType', ([bannerType], schema) => {
        if (bannerType !== BannerType.OTHER) return schema;

        return schema
          .test('languageRequired', t('common.inputValidations.languageRequired'), (value) =>
            Boolean(value?.en?.trim()),
          )
          .test('maxLength', t('common.inputValidations.maxLength', { len: DefaultTextboxLength }), (value) => {
            if (!value) return true;
            return Object.values(value).every((message) => (message?.length ?? 0) <= DefaultTextboxLength);
          });
      }),
    }),
    onSubmit: (values) => {
      dispatch(updateBannerForm(values));
      nextStep();
    },
  });

  /**
   * DateTime fields from MUI don't have an onBlur for formik to know the input was touched, so we need to manually set the field value and validate it
   * @param field
   * @param newValue
   */
  const handleDateChange = async (field: string, newValue: DateTime | null, pairedField: string) => {
    await formik.setFieldValue(field, newValue?.toISO() ?? null);
    await formik.validateField(field);

    if (pairedField && getIn(formik.values, pairedField)) {
      formik.setFieldTouched(pairedField, true);
    }
  };

  /**
   * Custom handler for date input
   * @param field
   */
  const handleDateBlur = (field: string) => {
    formik.setFieldTouched(field, true);
  };

  useEffect(() => {
    if (!editMode) return;

    (['startDate', 'endDate', 'maintenanceStart', 'maintenanceEnd'] as const).forEach((field) => {
      if (initialFormData[field]) {
        formik.setFieldTouched(field, true);
      }
    });
  }, [editMode, initialFormData]);

  const bannerTypeOptions: BannerTypeOption[] = [
    {
      value: BannerType.PLANNED_CHANGE,
      icon: <BannerCalendarIcon />,
      title: t('pages.bannerOnboarding.bannerType.plannedChange'),
      description: t('pages.bannerOnboarding.bannerType.plannedChangeDescription'),
    },
    {
      value: BannerType.OUTAGE,
      icon: <BannerWarningIcon />,
      title: t('pages.bannerOnboarding.bannerType.outage'),
      description: t('pages.bannerOnboarding.bannerType.outageDescription'),
    },
    {
      value: BannerType.OTHER,
      icon: <BannerPencilIcon />,
      title: t('pages.bannerOnboarding.bannerType.other'),
      description: t('pages.bannerOnboarding.bannerType.otherDescription'),
    },
  ];

  const scopeOptions: BannerScopeOption[] = [
    {
      value: BannerScope.GLOBAL,
      title: t('pages.bannerOnboarding.scope.global'),
      description: t('pages.bannerOnboarding.scope.globalDescription'),
    },
    {
      value: BannerScope.MARKETS,
      title: t('pages.bannerOnboarding.scope.markets'),
      description: t('pages.bannerOnboarding.scope.marketsDescription'),
    },
    {
      value: BannerScope.COMPANIES,
      title: t('pages.bannerOnboarding.scope.companies'),
      description: t('pages.bannerOnboarding.scope.companiesDescription'),
    },
  ];

  const visibleMarkets: Market[] = useMemo(() => {
    if (formik.values.scope === BannerScope.GLOBAL) return markets;

    if (formik.values.scope === BannerScope.MARKETS) {
      const selectedMarketIds = new Set(formik.values.selectedMarkets);
      return markets.filter((market) => selectedMarketIds.has(market.id));
    }

    if (formik.values.scope === BannerScope.COMPANIES) {
      const selectedCompanyIds = new Set(formik.values.selectedCompanies);
      const selectedCountryCodes = new Set<string>();
      companies.forEach((company) => {
        if (selectedCompanyIds.has(company.id)) selectedCountryCodes.add(company.country);
      });
      return markets.filter((market) => selectedCountryCodes.has(market.countryCode));
    }

    return [];
  }, [formik.values.scope, formik.values.selectedMarkets, formik.values.selectedCompanies, markets, companies]);

  const startDateFeedback =
    formik.values.bannerType !== '' && formik.values.bannerType !== BannerType.PLANNED_CHANGE
      ? t(`pages.bannerOnboarding.schedule.startDateOptionalFeedback`)
      : '';
  const endDateFeedback =
    formik.values.bannerType !== '' && formik.values.bannerType !== BannerType.PLANNED_CHANGE
      ? t(`pages.bannerOnboarding.schedule.endDateOptionalFeedback`)
      : '';

  // a draft needs at least the banner type and a valid title
  const canSaveDraft =
    Boolean(formik.values.bannerType) && Boolean(formik.values.bannerTitle.trim()) && !formik.errors.bannerTitle;

  /**
   * Check if section 2 is complete
   */
  const isScopeSectionSelected = (() => {
    if (formik.values.scope === BannerScope.GLOBAL) return true;
    if (formik.values.scope === BannerScope.MARKETS) return formik.values.selectedMarkets.length > 0;
    if (formik.values.scope === BannerScope.COMPANIES) return formik.values.selectedCompanies.length > 0;
    return false;
  })();

  /**
   * Check if section 3 is complete
   */
  const isScheduleSectionSelected = (() => {
    if (!formik.values.bannerTitle) return false;
    if (formik.values.bannerType !== BannerType.PLANNED_CHANGE) return true;

    const hasTimezone = Boolean(formik.values.timezone) && !formik.errors.timezone;
    const hasStartDate = Boolean(formik.values.startDate) && !formik.errors.startDate;
    const hasEndDate = Boolean(formik.values.endDate) && !formik.errors.endDate;
    return hasTimezone && hasStartDate && hasEndDate;
  })();

  /**
   * Check if section 4 (Maintenance window) is complete
   */
  const isMaintenanceSectionSelected = (() => {
    const hasTimezone = Boolean(formik.values.maintenanceTimezone) && !formik.errors.maintenanceTimezone;
    const hasStartDate = Boolean(formik.values.maintenanceStart) && !formik.errors.maintenanceStart;
    const hasEndDate = Boolean(formik.values.maintenanceEnd) && !formik.errors.maintenanceEnd;
    return hasTimezone && hasStartDate && hasEndDate;
  })();

  return (
    <Box sx={{ marginBottom: '3.75rem' }}>
      <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            boxShadow: '0px 0px 10px 1px rgba(0, 0, 0, 0.08)',
            borderRadius: '0.75rem',
            marginBottom: '3rem',
          }}
        >
          <FormSection
            number={1}
            title={t('pages.bannerOnboarding.bannerType.title')}
            description={t('pages.bannerOnboarding.bannerType.description')}
            selected={formik.values.bannerType !== ''}
          >
            <Grid container columnSpacing="1rem" rowSpacing="2rem">
              {bannerTypeOptions.map((option) => (
                <BannerOptionCard
                  key={option.value}
                  icon={option.icon}
                  title={option.title}
                  description={option.description}
                  selected={formik.values.bannerType === option.value}
                  onSelect={() => formik.setFieldValue('bannerType', option.value)}
                />
              ))}
            </Grid>
          </FormSection>

          <Divider sx={{ borderColor: theme.palette.monochrome.monoChrome3 }} />

          <FormSection
            number={2}
            title={t('pages.bannerOnboarding.scope.title')}
            description={t('pages.bannerOnboarding.scope.description')}
            selected={isScopeSectionSelected}
          >
            <Grid container columnSpacing="1rem" rowSpacing="2rem">
              {scopeOptions.map((option) => (
                <BannerOptionCard
                  key={option.value}
                  title={option.title}
                  description={option.description}
                  selected={formik.values.scope === option.value}
                  onSelect={() => formik.setFieldValue('scope', option.value)}
                />
              ))}
            </Grid>

            {formik.values.scope === BannerScope.MARKETS && (
              <Box sx={{ marginTop: '2rem' }}>
                <MultipleDropdown
                  name="selectedMarkets"
                  label={t('pages.bannerOnboarding.scope.markets')}
                  items={marketOptions}
                  placeholder={
                    t('common.placeholders.dropdownPlaceholder') +
                    t('pages.bannerOnboarding.scope.markets').toLowerCase()
                  }
                  value={formik.values.selectedMarkets}
                  onChange={(e) => formik.setFieldValue('selectedMarkets', e.target.value)}
                />
              </Box>
            )}

            {formik.values.scope === BannerScope.COMPANIES && (
              <Box sx={{ marginTop: '2rem' }}>
                <MultipleDropdown
                  name="selectedCompanies"
                  label={t('pages.bannerOnboarding.scope.companies')}
                  items={companiesOptions}
                  placeholder={
                    t('common.placeholders.dropdownPlaceholder') +
                    t('pages.bannerOnboarding.scope.companies').toLowerCase()
                  }
                  value={formik.values.selectedCompanies}
                  onChange={(e) => formik.setFieldValue('selectedCompanies', e.target.value)}
                />
              </Box>
            )}
          </FormSection>

          <Divider sx={{ borderColor: theme.palette.monochrome.monoChrome3 }} />

          <FormSection
            number={3}
            title={t('pages.bannerOnboarding.schedule.title')}
            description={t('pages.bannerOnboarding.schedule.description')}
            selected={isScheduleSectionSelected}
          >
            <TextInput
              name="bannerTitle"
              label={t('pages.bannerOnboarding.schedule.bannerTitle')}
              placeholder={t('pages.bannerOnboarding.schedule.bannerTitlePlaceholder')}
              value={formik.values.bannerTitle}
              onInputChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.bannerTitle && Boolean(formik.errors.bannerTitle)}
              feedback={formik.touched.bannerTitle && formik.errors.bannerTitle ? formik.errors.bannerTitle : ''}
              formStyle={{ paddingBottom: '1.5rem' }}
            />
            <Grid container columnSpacing="1.5rem" rowSpacing="1rem">
              <Grid item xl={4} md={4} xs={12}>
                <Dropdown
                  name="timezone"
                  label={t('pages.bannerOnboarding.schedule.timezone')}
                  items={timezoneOptions}
                  placeholder={t('pages.bannerOnboarding.schedule.timezonePlaceholder')}
                  value={formik.values.timezoneOption}
                  onChange={(e) => {
                    const selectedOption = timezoneOptions.find((option) => option.value === e.target.value);
                    formik.setFieldValue('timezoneOption', selectedOption?.value ?? '');
                    formik.setFieldValue('timezone', selectedOption?.data ?? '');
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.timezone && Boolean(formik.errors.timezone)}
                  feedback={formik.touched.timezone && formik.errors.timezone ? formik.errors.timezone : ''}
                />
              </Grid>
              <Grid item xl={4} md={4} xs={12}>
                <DateTimeCalendar
                  label={t('pages.bannerOnboarding.schedule.startDate')}
                  value={toDateTimeValue(formik.values.startDate)}
                  onChange={(newValue) => handleDateChange('startDate', newValue, 'endDate')}
                  onBlur={() => handleDateBlur('startDate')}
                  error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                  feedback={
                    formik.touched.startDate && formik.errors.startDate
                      ? String(formik.errors.startDate)
                      : startDateFeedback
                  }
                  optional={formik.values.bannerType !== '' && formik.values.bannerType !== BannerType.PLANNED_CHANGE}
                />
              </Grid>
              <Grid item xl={4} md={4} xs={12}>
                <DateTimeCalendar
                  label={t('pages.bannerOnboarding.schedule.endDate')}
                  value={toDateTimeValue(formik.values.endDate)}
                  onChange={(newValue) => handleDateChange('endDate', newValue, 'startDate')}
                  onBlur={() => handleDateBlur('endDate')}
                  error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                  feedback={
                    formik.touched.endDate && formik.errors.endDate ? String(formik.errors.endDate) : endDateFeedback
                  }
                  optional={formik.values.bannerType !== '' && formik.values.bannerType !== BannerType.PLANNED_CHANGE}
                />
              </Grid>
            </Grid>

            {formik.values.timezone && visibleMarkets.length > 0 && (
              <TimeZoneConversionTable
                baseTimezone={formik.values.timezone}
                startDate={toDateTimeValue(formik.values.startDate)}
                endDate={toDateTimeValue(formik.values.endDate)}
                markets={visibleMarkets}
              />
            )}
          </FormSection>

          <Divider sx={{ borderColor: theme.palette.monochrome.monoChrome3 }} />

          {formik.values.bannerType === BannerType.PLANNED_CHANGE && (
            <>
              <FormSection
                number={4}
                title={t('pages.bannerOnboarding.maintenanceWindow.title')}
                description={t('pages.bannerOnboarding.maintenanceWindow.description')}
                selected={isMaintenanceSectionSelected}
              >
                <Grid container columnSpacing="1.5rem" rowSpacing="1rem">
                  <Grid item xl={4} md={4} xs={12}>
                    <Dropdown
                      name="maintenanceTimezone"
                      label={t('pages.bannerOnboarding.schedule.timezone')}
                      items={timezoneOptions}
                      placeholder={t('pages.bannerOnboarding.schedule.timezonePlaceholder')}
                      value={formik.values.maintenanceTimezoneOption}
                      onChange={(e) => {
                        const selectedOption = timezoneOptions.find((option) => option.value === e.target.value);
                        formik.setFieldValue('maintenanceTimezoneOption', selectedOption?.value ?? '');
                        formik.setFieldValue('maintenanceTimezone', selectedOption?.data ?? '');
                      }}
                      error={formik.touched.maintenanceTimezone && Boolean(formik.errors.maintenanceTimezone)}
                      feedback={
                        formik.touched.maintenanceTimezone && formik.errors.maintenanceTimezone
                          ? formik.errors.maintenanceTimezone
                          : ''
                      }
                    />
                  </Grid>
                  <Grid item xl={4} md={4} xs={12}>
                    <DateTimeCalendar
                      label={t('pages.bannerOnboarding.maintenanceWindow.maintenanceStart')}
                      value={toDateTimeValue(formik.values.maintenanceStart)}
                      onChange={(newValue) => handleDateChange('maintenanceStart', newValue, 'maintenanceEnd')}
                      onBlur={() => handleDateBlur('maintenanceStart')}
                      error={formik.touched.maintenanceStart && Boolean(formik.errors.maintenanceStart)}
                      feedback={
                        formik.touched.maintenanceStart && formik.errors.maintenanceStart
                          ? formik.errors.maintenanceStart
                          : ''
                      }
                    />
                  </Grid>
                  <Grid item xl={4} md={4} xs={12}>
                    <DateTimeCalendar
                      label={t('pages.bannerOnboarding.maintenanceWindow.maintenanceEnd')}
                      value={toDateTimeValue(formik.values.maintenanceEnd)}
                      onChange={(newValue) => handleDateChange('maintenanceEnd', newValue, 'maintenanceStart')}
                      onBlur={() => handleDateBlur('maintenanceEnd')}
                      error={formik.touched.maintenanceEnd && Boolean(formik.errors.maintenanceEnd)}
                      feedback={
                        formik.touched.maintenanceEnd && formik.errors.maintenanceEnd
                          ? formik.errors.maintenanceEnd
                          : ''
                      }
                    />
                  </Grid>
                </Grid>
              </FormSection>

              <Divider sx={{ borderColor: theme.palette.monochrome.monoChrome3 }} />
            </>
          )}

          <FormSection
            number={formik.values.bannerType === BannerType.PLANNED_CHANGE ? 5 : 4}
            title={t('pages.bannerOnboarding.message.title')}
            description={t('pages.bannerOnboarding.message.description')}
            selected={formik.isValid}
          >
            {formik.values.bannerType ? (
              <MessagePreview
                bannerType={formik.values.bannerType}
                startDate={toDateTimeValue(formik.values.maintenanceStart || formik.values.startDate)}
                endDate={toDateTimeValue(formik.values.maintenanceEnd || formik.values.endDate)}
                messages={formik.values.messages}
                onMessageChange={(language, value) => {
                  const updatedMessages = { ...formik.values.messages };
                  if (value) {
                    updatedMessages[language] = value;
                  } else {
                    delete updatedMessages[language];
                  }
                  formik.setFieldValue('messages', updatedMessages);
                }}
                onBlur={() => formik.setFieldTouched('messages', true)}
                touched={Boolean(formik.touched.messages)}
              />
            ) : (
              <Box
                sx={{
                  minHeight: '8rem',
                  padding: '1rem 1.25rem',
                  border: `1px solid ${theme.palette.monochrome.monoChrome3}`,
                  borderRadius: '0.5rem',
                  marginBottom: '2.5rem',
                }}
              >
                <Typography sx={{ color: theme.palette.monochrome.monoChrome4 }}>
                  {t('pages.bannerOnboarding.message.previewPlaceholder')}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Switch
                checked={formik.values.dismissible}
                onChange={(e) => formik.setFieldValue('dismissible', e.target.checked)}
              />
              <Box>
                <Typography sx={{ fontSize: '1.125rem', lineHeight: '100%' }}>
                  {t('pages.bannerOnboarding.message.dismissible')}
                </Typography>
                <Typography sx={{ fontSize: '1rem', color: theme.palette.text.disabled }}>
                  {t('pages.bannerOnboarding.message.dismissibleDescription')}
                </Typography>
              </Box>
            </Box>
          </FormSection>
        </Box>

        <ButtonGroup sx={{ justifyContent: 'flex-end', paddingTop: '1.5rem' }}>
          <Button label={t('common.cancel')} color="alternative2Button" type="button" onClick={onClose} />
          {!editMode ? (
            <Button
              label={t('pages.bannerOnboarding.saveDraft')}
              color="alternative1Button"
              type="button"
              onClick={() => onSaveDraft(formik.values)}
              loading={isLoading}
              disabled={!canSaveDraft || isLoading || sessionStorage.getItem('impersonateId') !== null}
            />
          ) : null}
          <Button
            label={t('pages.bannerOnboarding.reviewAndPublish')}
            color="primaryButton"
            type="submit"
            disabled={!formik.isValid || sessionStorage.getItem('impersonateId') !== null}
          />
        </ButtonGroup>
      </form>
    </Box>
  );
}

export default BannersFormContainer;
